/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
  ChangeDetectionStrategy
} from '@angular/core';
import {
    forkJoin,
    merge,
    Subject,
    Subscription
} from 'rxjs';
import {Router} from '@angular/router';
import {PartNumberChangeCommandModel} from '../../api/ms-partnumberchanges/contracts/part-number-change-command.model';
import {PartNumberChangesApi} from '../../api/ms-partnumberchanges/part-number-changes.api';
import {ToastWrapperService} from '../../shared/utils/toast-wrapper-service/toast-wrapper-service';
import {UnsavedChangesService} from '../../shared/guards/unsaved-work/unsaved-changes.service';
import {ReplayableRouterEventsService} from '../../shared/services/replayable-router-events.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {StatefulComponent} from '../../shared/stateful/stateful.component';
import {ConfirmModalComponent} from '../../shared/confirm-modal/confirm-modal/confirm-modal.component';
import {InputRadioComponent} from '../../shared/input/input-radio/input-radio.component';
import {PncTypeModel} from '../models/pnc-type-model/pnc-type.model';
import {PncCreateNewState} from './models/pnc-create-new.state';
import {AlertNotificationSeverity} from '../../alert-notification-severity.model';
import {PncTypeModelMapper} from '../models/pnc-type-model/pnc-type.model.mapper';
import {InputControl} from '../../shared/input/input/input-control';
import {
    finalize,
    take
} from 'rxjs/operators';
import {PartNumberChangesCredentialsStateModel} from '../part-number-changes-credentials/part-number-changes-credentials-state.model';
import {PartNumberChangesDetailFooterActionsEnum} from '../models/part-number-changes-detail-footer-actions.enum';
import {PartNumberChangesDisplayConfigurationService} from '../services/part-number-changes-detail-configuration/part-number-changes-display-configuration.service';
import {PartNumberChangesPageTypeEnum} from '../services/part-number-changes-detail-configuration/models/part-number-changes-page-type.enum';
import {PartNumberChangesDetailComponentsModel} from '../services/part-number-changes-detail-configuration/models/part-number-changes-detail-components.model';
import {PartNumberChangesCreateComponentsService} from '../services/part-number-changes-create-components/part-number-changes-create-components.service';
import {PartNumberChangesDisplayComponentsTypeEnum} from '../services/part-number-changes-detail-configuration/models/part-number-changes-display-components-type.enum';
import {PncTypeEnum} from '../models/pnc-type-enum/pnc-type.enum';
import {RouteSearchCriteriaModel} from '../../api/ms-partnumberchanges/contracts/route-search-criteria.model';
import {PartNumberChangesOldCredentialsFormState} from '../models/part-number-changes-old-credentials-form.state';
import {SpinnerService} from '../../shared/spinner/spinner.service';
import {SpinnerContextTypeEnum} from '../../shared/spinner/spinner-context-type.enum';
import {PartNumberChangesDetailSolutionState} from '../part-number-changes-solution/part-number-changes-detail-solution.state';
import {PartNumberChangeSolveCommandModel} from '../../api/ms-partnumberchanges/contracts/part-number-change-solve-command.model';
import {PartNumberChangeCreateSolveContract} from '../../api/ms-partnumberchanges/contracts/part-number-change-create-solve.contract';
import {PartNumberChangeBaseModel} from '../../api/ms-partnumberchanges/contracts/part-number-change-base.model';
import {PncSolutionUtilService} from '../services/pnc-solution-util/pnc-solution-util.service';
import {PncCreateActionEnum} from './models/pnc-create-action.enum';
import {PartNumberChangesCreateNewService} from '../services/part-number-changes-create-new.service';
import {PartNumberChangesAiSolutionState} from '../part-number-changes-ai-solution/part-number-changes-ai-solution.state';
import {StatusTimelineConfigurationModel} from '../../shared/status-timeline/models/status-timeline-configuration.model';
import {StateConfigurationModel} from '../../shared/status-timeline/models/state-configuration.model';
import {HistoryStateConfigurationModel} from '../../shared/status-timeline/models/history-state-configuration.model';
import timelineHappyPath from './configuration/timeline-happypath-configuration';
import {PartNumberChangesUpdateAiMaterialModel} from '../part-number-changes-ai-solution/models/part-number-changes-update-ai-material.model';
import {toBase64Utf16LittleEndian} from '../../shared/utils/encode/encodeToBase64Utf16LE';
import {SupplyingPackingPlantBaseModel} from "../../api/ms-specialorders/contracts/supplying-packing-plant-base.model";

@Component({
    selector: 'app-part-number-changes-create-new',
    templateUrl: './part-number-changes-create-new.component.html',
    styleUrls: ['../styles/pnc-base.component.css', './part-number-changes-create-new.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PartNumberChangesCreateNewComponent
        extends StatefulComponent<PncCreateNewState>
        implements OnInit, AfterViewInit, OnDestroy {
    /** Guards Variables **/
    public MODAL_TITLE = 'PNC yet';
    public isExited = false;

    pncTypes: PncTypeModel[];

    @ViewChild('pncTypeInput') pncTypeInput: InputRadioComponent<PncTypeModel>;
    @ViewChild('credentialsContainer', {read: ViewContainerRef, static: true}) credentialsContainer: ViewContainerRef;
    @ViewChild('oldPartNumberContainer', {
        read: ViewContainerRef,
        static: true
    }) oldPartNumberContainer: ViewContainerRef;
    @ViewChild('solutionContainer', {read: ViewContainerRef, static: true}) solutionContainer: ViewContainerRef;
    @ViewChild('footerContainer', {read: ViewContainerRef, static: true}) footerContainer: ViewContainerRef;

    public routeSearchCriteriaChange = new Subject<RouteSearchCriteriaModel>();
    public oldMaterialChange = new Subject<PartNumberChangesUpdateAiMaterialModel>();
    // private alternativeSupplyingPackingPlantCodes: string[];
    private alternativeSupplyingPackingPlants: SupplyingPackingPlantBaseModel[];

    private resetForm: Subject<void> = new Subject<void>();

    private readonly CREATE_ACTION_LABEL = 'Create';
    private readonly CREATE_AND_SOLVE_ACTION_LABEL = 'Create and send solution';
    private readonly stateInitialColor = '#444444';
    private readonly timelineBackgroundColor = '#f6f6f6';

    private componentsConfiguration: PartNumberChangesDetailComponentsModel[];
    private subscriptions = new Subscription();
    private partNumberChangeInfo: PartNumberChangeCommandModel = null;
    private partNumberChangeSolutionInfo: PartNumberChangeSolveCommandModel = null;

    public spinnerObservable = this.spinnerService.onLocalChange(this);

    get hasPncTypes(): boolean {
        return (this.pncTypes?.length ?? 0) > 1;
    }

    get hasAnyPncType(): boolean {
        return (this.pncTypes?.length ?? 0) >= 1;
    }

    get isValidToCreate(): boolean {
        let isValid = this.state.credentialsSectionIsValid && this.state.oldPartNumberSectionIsValid;
        if (this.state.type?.code === PncTypeEnum.AI_CHANGE) {
            isValid = isValid && this.state.solutionSectionIsValid;
        }
        return isValid;
    }

    get initialOption(): PncTypeModel {
        return this.pncTypes?.[0];
    }

    get userInputPncInfo(): PartNumberChangeCommandModel {
        let userPnc: PartNumberChangeCommandModel = null;

        if (this.partNumberChangeInfo != null) {
            userPnc = {...this.partNumberChangeInfo};

            // Although it's needed for the back-end, we want to ignore the Type selection on the front-end guards
            delete userPnc.typeCode;
        }

        return userPnc;
    }

    get pncTypeCssClass(): string {
        return this.state?.type?.code ?? PncTypeEnum.UNKNOWN;
    }

    constructor(
            public spinnerService: SpinnerService,
            private partNumberChangesService: PartNumberChangesApi,
            private router: Router,
            private toast: ToastWrapperService,
            private modalService: NgbModal,
            private unsavedChangesService: UnsavedChangesService,
            private replay: ReplayableRouterEventsService,
            private displayConfiguration: PartNumberChangesDisplayConfigurationService,
            private componentsService: PartNumberChangesCreateComponentsService,
            private cdr: ChangeDetectorRef,
            private solutionUtil: PncSolutionUtilService,
            private partNumberChangesCreateNewService: PartNumberChangesCreateNewService) {
        super();

        this.spinnerService.start(SpinnerContextTypeEnum.GLOBAL, PartNumberChangesCreateNewComponent);
        this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, null);
    }

    additionalLotNumberPncInfo(oldPartNumberCredentials: PartNumberChangesOldCredentialsFormState): void {
        if (oldPartNumberCredentials.additionalFullLotNumbers != null) {
            this.partNumberChangeInfo = {
                ...this.partNumberChangeInfo,
                additionalFullLotNumbers: oldPartNumberCredentials.additionalFullLotNumbers
            };
        }
    }

    pncTypeTransform(value: PncTypeModel): string {
        return value.name;
    }

    ngOnInit(): void {
        this.unsavedChangesService.originalValue = {} as PartNumberChangeCommandModel;

        const getConfig = this.partNumberChangesService.getPartNumberChangeConfig();
        const getTypes = this.partNumberChangesService.getAvailablePartNumberChangeTypeBase();

        this.subscriptions.add(
                this.replay.onGuardsCheckStart.subscribe(() => {
                    this.updateCurrentState();
                })
        );

        this.subscriptions.add(forkJoin([getConfig, getTypes])
                .pipe(finalize(() => {
                    this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, PartNumberChangesCreateNewComponent);
                }))
                .subscribe(([config, types]) => {
                    this.storeState({partNumberChangesConfiguration: config});
                    this.pncTypes = PncTypeModelMapper.fromPartNumberChangeTypeBaseModelArray(types.filter((type) => type.active));
                    if (!this.hasPncTypes) {
                        this.updatePncType(this.pncTypes[0]);
                    }
                    this.cdr.detectChanges();
                }));

        this.partNumberChangesCreateNewService.alternativeSupplyingPackingPlants$.subscribe(codes => {
            this.alternativeSupplyingPackingPlants = codes;
        });
    }

    ngAfterViewInit(): void {
        this.updateDisplayConfiguration();
        this.cdr.detectChanges();
    }

    ngOnDestroy(): void {
        this.componentsService.destroyComponents();
        this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, PartNumberChangesCreateNewComponent);
        this.subscriptions.unsubscribe();
    }

    manageRouteCriteriaChange = (routeCriteria: RouteSearchCriteriaModel): void => {
        this.routeSearchCriteriaChange.next(routeCriteria);
    };

    manageOldMaterialChange = (materialAiChange: PartNumberChangesUpdateAiMaterialModel): void => {
        this.oldMaterialChange.next(materialAiChange);
    };

    credentialsSectionIsValidForCreate = (isValid: boolean): void => {
        this.storeState({credentialsSectionIsValid: isValid});
        this.setButtonActions();
    };

    oldPartNumberSectionIsValidForCreate = (isValid: boolean): void => {
        this.storeState({oldPartNumberSectionIsValid: isValid});
        this.setButtonActions();
    };

    formIsLoading = (loading: boolean): void => {
        if (loading) {
            this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this);
        } else {
            this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this);
        }
    };

    setCredentialsFormValue = (credentials: PartNumberChangesCredentialsStateModel): void => {
        this.partNumberChangeInfo = {
            ...this.partNumberChangeInfo,
            partnerCode: credentials.partner?.code,
            shipToPartyCode: credentials.shipToParty?.code,
            supplyingPackingPlantCode: credentials?.supplyingPackingPlant?.code,
            alternativeSupplyingPackingPlants: this.alternativeSupplyingPackingPlants
        };

        this.hasUserInputHandler();
    };

    setOldPartNumberFormValue = (oldPartNumberCredentials: PartNumberChangesOldCredentialsFormState): void => {
        this.partNumberChangeInfo = {
            ...this.partNumberChangeInfo,
            materialNumber: oldPartNumberCredentials?.material?.number,
            salesVariant: oldPartNumberCredentials?.salesVariant,
            modelCode: oldPartNumberCredentials?.modelCode,
            colourCode: oldPartNumberCredentials?.colourCode,
            fullLotNumber: oldPartNumberCredentials?.fullLotNumber,
            quantity: oldPartNumberCredentials?.quantity,
            problemDescription: toBase64Utf16LittleEndian(oldPartNumberCredentials?.problemDescription),
            additionalFullLotNumbers: oldPartNumberCredentials?.additionalFullLotNumbers,
            spoNumber: oldPartNumberCredentials?.spoNumber?.spoNumber,
            additionalSpoNumbers: oldPartNumberCredentials?.additionalSpoNumbers?.map((spoNumber) => spoNumber.spoNumber)
        };
        this.hasUserInputHandler();
        this.additionalLotNumberPncInfo(oldPartNumberCredentials);
    };

    discard(): void {
        this.router.navigate(['/', 'pnc']);
    }

    createAction(): void {
        if (this.partNumberChangeInfo != null) {
            this.partNumberChangeInfo.typeCode = this.state?.type?.code;
        }

        if (this.state.type?.code === PncTypeEnum.AI_CHANGE) {
            this.createModal(PncCreateActionEnum.CREATE_AND_SOLVE);
        } else {
            this.createModal(PncCreateActionEnum.CREATE);
        }
    }

    footerAction = (action: PartNumberChangesDetailFooterActionsEnum): void => {
        switch (action) {
            case PartNumberChangesDetailFooterActionsEnum.CREATE:
                this.createAction();
                break;
            case PartNumberChangesDetailFooterActionsEnum.DISCARD_CREATE:
                this.discard();
                break;
            default:
                return;
        }
    };

    pncTypeStateChange(event: InputControl<PncTypeModel>): void {
        if (!this.state.isGuardingPncTypeChange) {
            this.updatePncType(event?.value);
        }
    }

    pncTypeValueChange(event: PncTypeModel): void {
        if (!this.state.isGuardingPncTypeChange) {
            if (this.state.hasUserInput) {
                this.openChangeTypeConfirmationModal(event);
            } else {
                this.updatePncType(event);
            }
        }
    }

    /**
     * Converts the PartNumberChangePreviousStatusModel[] into a StatusTimelineConfigurationModel
     */
    mapStatusTimelineToConfigurationModel(): StatusTimelineConfigurationModel {
        const configuration: StatusTimelineConfigurationModel = {
            backgroundColor: this.timelineBackgroundColor,
            happyPath: this.getStatusTimelineHappyPath(this.state?.type),
            statusHistory: this.getStatusHistory(this.state?.type)
        };

        return configuration;
    }

    /**
     * For a given PNC type returns its status timeline happy path model
     */
    private getStatusTimelineHappyPath(pncType: PncTypeModel): StateConfigurationModel[] {
        const happyPath: StateConfigurationModel[] = [];

        if (pncType?.code === PncTypeEnum.PLANNER) {
            happyPath.push(
                    timelineHappyPath.happyPathPending
            );
        }

        if (pncType?.code !== PncTypeEnum.AI_CHANGE) {
            happyPath.push(
                    timelineHappyPath.happyPathAnalysis
            );
        }

        happyPath.push(
                timelineHappyPath.happyPathSolved
        );

        return happyPath;
    }

    /**
     * For a given PNC type returns its status history
     */
    private getStatusHistory(pncHistory: PncTypeModel): HistoryStateConfigurationModel[] {
        const initialStatus: HistoryStateConfigurationModel[] = [
            {
                state: {
                    name: pncHistory?.name,
                    code: pncHistory?.code,
                    color: this.stateInitialColor
                }
            }
        ];

        return initialStatus;
    };

    private createModal(action: PncCreateActionEnum): void {
        const createCallback = action === PncCreateActionEnum.CREATE ? this.create : this.createAndSolve;
        const titleAndActionLabel = action === PncCreateActionEnum.CREATE ? this.CREATE_ACTION_LABEL : this.CREATE_AND_SOLVE_ACTION_LABEL;

        const confirmModalInstance: ConfirmModalComponent = this.modalService.open(ConfirmModalComponent).componentInstance;
        confirmModalInstance.modalConfiguration = {
            body: {
                width: 406,
                manualChecks: {
                    title: 'Be aware that:',
                    options: ['You will not be able to undo this action.']
                }
            },
            title: titleAndActionLabel,
            primaryActionTitle: titleAndActionLabel,
            secondaryActionTitle: 'Discard',
            alertNotificationSeverity: AlertNotificationSeverity.INFORMATION
        };

        confirmModalInstance.primaryEvent.pipe(take(1))
                .subscribe(() => {
                    createCallback();
                });
    }

    private create = (): void => {
        this.spinnerService.start(SpinnerContextTypeEnum.GLOBAL, PartNumberChangesCreateNewComponent);
        this.subscriptions.add(
                this.partNumberChangesService.createPartNumberChange(this.partNumberChangeInfo)
                        .subscribe((newPncNumber) => {
                            this.createSuccess(newPncNumber, PncCreateActionEnum.CREATE);
                        }, (error) => this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, PartNumberChangesCreateNewComponent))
        );
    };

    private createAndSolve = (): void => {
        const createAndSolveCommand: PartNumberChangeCreateSolveContract = {
            partNumberChange: this.partNumberChangeInfo,
            solution: this.partNumberChangeSolutionInfo
        };
        this.spinnerService.start(SpinnerContextTypeEnum.GLOBAL, PartNumberChangesCreateNewComponent);
        this.subscriptions.add(
                this.partNumberChangesService.createAndSolvePartNumberChange(createAndSolveCommand)
                        .subscribe((newPncNumber) => {
                            this.createSuccess(newPncNumber, PncCreateActionEnum.CREATE_AND_SOLVE);
                        }, () => this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, PartNumberChangesCreateNewComponent))
        );
    };

    private createSuccess(newPncNumber: PartNumberChangeBaseModel, action: PncCreateActionEnum): void {
        this.isExited = true;
        const toastActionLabel = action === PncCreateActionEnum.CREATE ? 'created' : 'sent';
        this.toast.success(
                `The PNC <span class="toast-new-pnc-number">${newPncNumber.number}</span> was successfully ${toastActionLabel}.`,
                'Success');
        this.partNumberChangesCreateNewService.onCreated.next(newPncNumber.number);
        this.router.navigate(['/', 'pnc']);
    }

    private updatePncType(type: PncTypeModel): void {
        this.storeState({
            type
        });

        this.updateDisplayConfiguration();
    }

    private updateDisplayConfiguration(): void {
        this.componentsConfiguration = this.displayConfiguration
                .retrievePNCDisplayConfiguration(PartNumberChangesPageTypeEnum.CREATE, null, this.state, this.state?.type?.code);
        this.componentsService.updateComponents(this.createComponents, this.componentsConfiguration);
    }

    private updateCurrentState(): void {
        this.unsavedChangesService.currentValue = this.userInputPncInfo;
    }

    private hasUserInputHandler(): void {
        let hasUserInput = false;

        for (const prop in this.userInputPncInfo) {
            // This is a bit more extensive and less direct than obj.hasOwnProperty, but it's safer than the first alternative
            if (Object.prototype.hasOwnProperty.call(this.userInputPncInfo, prop)) {
                // Forcing typecast to never and losing type-safety, otherwise we can't use a loop over an object
                hasUserInput = hasUserInput || (this.userInputPncInfo as never)[prop] != null;
            }
        }

        this.storeState({hasUserInput});
    }

    private openChangeTypeConfirmationModal(nextType: PncTypeModel): void {
        this.storeState({
            isGuardingPncTypeChange: true
        });

        this.pncTypeInput.setValue(this.state.type);

        const confirmModalInstance: ConfirmModalComponent = this.modalService.open(ConfirmModalComponent).componentInstance;

        confirmModalInstance.modalConfiguration = {
            body: {
                width: 420,
                title: 'Are you sure you want to change the PNC Type? All the inserted information will be removed.'
            },
            title: 'Confirm',
            primaryActionTitle: 'Change PNC type',
            secondaryActionTitle: 'Stay on this PNC type',
            alertNotificationSeverity: AlertNotificationSeverity.INFORMATION
        };

        // Must be subscribed BEFORE the guard release below, so the guard is still active while the type is changed.
        confirmModalInstance.primaryEvent.pipe(take(1))
                .subscribe(() => {
                    this.resetForm.next();
                    this.pncTypeInput.setValue(nextType);
                    // The guard ignores the radio's state change events, so update the type explicitly
                    this.updatePncType(nextType);
                    this.storeState({hasUserInput: false});
                    this.partNumberChangeInfo = null;
                });

        // Same behaviour on modal close, so it doesn't matter which event emitted
        merge(confirmModalInstance.primaryEvent, confirmModalInstance.secondaryEvent, confirmModalInstance.closeEvent)
                .pipe(take(1))
                .subscribe(() => {
                    this.storeState({
                        isGuardingPncTypeChange: false
                    });
                });
    }

    private setButtonActions(): void {
        const footer = this.componentsConfiguration.find((component) =>
                component.type === PartNumberChangesDisplayComponentsTypeEnum.FOOTER);

        if (footer?.data?.primaryButton != null) {
            footer.data.primaryButton.disable = !this.isValidToCreate;
        }
    }

    private mapSolution = (solution: PartNumberChangesDetailSolutionState): void => {
        this.partNumberChangeSolutionInfo = {
            material: this.solutionUtil.convertSolutionMaterial(solution.material),
            additionalInformation: toBase64Utf16LittleEndian(solution.additionalInformation) ?? null,
            additionalMaterials: solution.additionalMaterials?.map((additional) =>
                    this.solutionUtil.convertSolutionMaterial(additional)) ?? null,
            solutionSupplyingPackingPlant: solution.solutionSupplyingPackingPlant
        };
        this.storeState({solutionSectionIsValid: solution.isValid});
        this.setButtonActions();
    };

    private mapAiSolution = (solution: PartNumberChangesAiSolutionState): void => {
        this.partNumberChangeSolutionInfo = {
            material: this.solutionUtil.convertSolutionMaterial(solution),
            additionalInformation: toBase64Utf16LittleEndian(solution.additionalInformation) ?? null,
            solutionSupplyingPackingPlant: null
        };
        this.storeState({solutionSectionIsValid: solution.isValid});
        this.setButtonActions();
    };

    private createComponents = (components: PartNumberChangesDetailComponentsModel[]): void => {
        components.forEach((item) => {
            switch (item.type) {
                case PartNumberChangesDisplayComponentsTypeEnum.CREDENTIALS:
                    this.componentsService.mapCredentialsSection(
                            item.data,
                            this.credentialsContainer,
                            this.manageRouteCriteriaChange,
                            this.formIsLoading,
                            this.setCredentialsFormValue,
                            this.credentialsSectionIsValidForCreate,
                            this.resetForm);
                    break;
                case PartNumberChangesDisplayComponentsTypeEnum.OLD_PART_NUMBER_CREATE:
                    this.componentsService.mapOldPartNumber(
                            item.data,
                            this.oldPartNumberContainer,
                            this.manageOldMaterialChange,
                            this.routeSearchCriteriaChange,
                            this.oldPartNumberSectionIsValidForCreate,
                            this.setOldPartNumberFormValue,
                            this.formIsLoading,
                            this.resetForm);
                    break;
                case PartNumberChangesDisplayComponentsTypeEnum.SOLUTION:
                    this.componentsService.mapSolutionSection(
                            item.data,
                            this.solutionContainer,
                            null,
                            null,
                            this.alternativeSupplyingPackingPlants,
                            this.mapSolution,
                            this.routeSearchCriteriaChange,
                            this.resetForm);
                    break;
                case PartNumberChangesDisplayComponentsTypeEnum.AI_SOLUTION:
                    this.componentsService.mapAiSolutionSection(
                            item.data,
                            this.solutionContainer,
                            null,
                            this.mapAiSolution,
                            this.routeSearchCriteriaChange,
                            this.oldMaterialChange,
                            this.formIsLoading,
                            this.resetForm);
                    break;
                case PartNumberChangesDisplayComponentsTypeEnum.SECTION_TITLE:
                    this.componentsService.mapTitleSection(item.data, this.solutionContainer);
                    break;
                case PartNumberChangesDisplayComponentsTypeEnum.FOOTER:
                    this.componentsService.mapFooterSection(item.data, this.footerContainer, this.footerAction);
                    break;
                default:
                    return;
            }
        });
    };
}
